import { DEFAULT_SEARCH_FIELDS, SearchFieldConfig } from 'features/search/types'
import get from 'lodash/get'
import {
  GeckoEntry,
  RolodexEntryType,
  RolodexHistogramFilter,
  RolodexHistogramQuery,
  RolodexHistogramResponse,
} from 'types'

export const buildIdsQueries = ({
  searchTerm,
  entityType,
  searchFields = DEFAULT_SEARCH_FIELDS,
  siteID,
  orgID,
}: {
  searchTerm: string
  entityType: RolodexEntryType
  searchFields?: SearchFieldConfig[]
  siteID: string
  orgID: string
}): RolodexHistogramQuery[] => {
  const filters: RolodexHistogramFilter[] = searchFields.map((config) => {
    return {
      field: config.field,
      key: config.key,
      value: searchTerm,
      op: '~',
    }
  })
  return [
    {
      type: entityType,
      advanced_filter: {
        operator: 'or',
        filters: filters,
      },
      select: [
        { field: 'type', label: 'type' },
        { field: 'id', label: 'id' },
      ],
      tags: {
        site: [siteID],
        organization: [orgID],
      },
      limit: 10000,
    },
  ]
}

export const getDaysSince = (dateString: string): number => {
  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  return Math.floor(diffMs / (1000 * 60 * 60 * 24))
}

export const calculateRelevanceScore = (
  entry: GeckoEntry,
  searchTerm: string,
  searchFields: SearchFieldConfig[] = DEFAULT_SEARCH_FIELDS
): number => {
  const lowerTerm = searchTerm.toLowerCase()
  let score = 0

  for (const fieldConfig of searchFields) {
    const fieldPath = fieldConfig.key
      ? `${fieldConfig.field}.${fieldConfig.key}`
      : fieldConfig.field

    const rawEntry = entry.raw
    const value = get(rawEntry, fieldPath)

    if (typeof value === 'string') {
      const lowerValue = value.toLowerCase()
      const weight = fieldConfig.weight ?? 50

      if (lowerValue === lowerTerm) {
        score += weight * 1.0
      } else if (lowerValue.startsWith(lowerTerm)) {
        score += weight * 0.75
      } else if (lowerValue.includes(lowerTerm)) {
        score += weight * 0.5
      }
    }
  }

  const daysSinceUpdate = getDaysSince(entry.updated_at)
  score += Math.max(0, 10 - daysSinceUpdate)

  return score
}

export const rankResults = <T extends GeckoEntry>(
  results: readonly T[],
  searchTerm: string,
  searchFields?: SearchFieldConfig[]
): Array<{ entry: T; relevanceScore: number }> => {
  return results
    .map((entry) => ({
      entry,
      relevanceScore: calculateRelevanceScore(entry, searchTerm, searchFields),
    }))
    .sort((a, b) => b.relevanceScore - a.relevanceScore)
}

export const extractIdsFromHistogram = (
  histogramData: RolodexHistogramResponse | undefined
): string[] => {
  if (!histogramData) return []

  return histogramData
    .map((row) => row.id as string)
    .filter((id): id is string => typeof id === 'string')
}

export const mergeAndDeduplicateIds = (
  histogramDataArrays: RolodexHistogramResponse[] | undefined
): string[] => {
  if (!histogramDataArrays || histogramDataArrays.length === 0) return []

  const allIds = new Set<string>()
  histogramDataArrays.forEach((histogramData) => {
    const ids = extractIdsFromHistogram(histogramData)
    ids.forEach((id) => allIds.add(id))
  })

  return Array.from(allIds)
}

export const extractCountsFromIds = (
  idsMap: Partial<Record<RolodexEntryType, Set<string>>>
): Partial<Record<RolodexEntryType, number>> => {
  const counts: Partial<Record<RolodexEntryType, number>> = {}
  Object.entries(idsMap).forEach(([type, ids]) => {
    counts[type as RolodexEntryType] = ids?.size ?? 0
  })
  return counts
}
